// watercolorBrush.js
import chroma from 'chroma-js';
import { brushDefaults } from './helpers/brushLoader';
import { fillGradient } from './helpers/gradient';
import { getStrokeBounds, translatePoints, calculatePathDistances, calculatePath, interpolatePointsUniform } from './helpers/points';

const defaultBrushSettings =  {
  opacity: 0.6,
  wobble: 0.08,
  density: 1.2,
  dryEdges: 0.7, // Strength of pigment accumulation at edges
  bloom: 0.3, // Soft diffusion effect
  granulation: 0.4, // Texture variation
  taperLength: 3,
  taperWidth: 0.2,
  flatEdges: false,
  autoRotation: true,
  angle: 0,
  edgeStep: 6,
  textureStrength: 0.0,
  baseScaleFactor: 40,
};

export async function drawTestStroke({
  color,
  gradientColor,
  lineWidth,
  points,
  time,
  sets = {},
}, context) {
  if (points.length === 0) return;

  const brushSettings = Object.assign({}, defaultBrushSettings, sets);
  const {
    opacity = 0.6,
    wobble = 0.08,
    density = 1.2,
    dryEdges = 0.7,
    bloom = 0.3,
    granulation = 0.4,
    taperLength = 3,
    taperWidth = 0.2,
    flatEdges = false,
    autoRotation = true,
    angle = 0,
    edgeStep = 6,
    textureStrength = 0.3,
    baseScaleFactor = 40,
  } = brushSettings;

  const chromaColor = chroma(color);
  const originalAlpha = chromaColor.alpha();

  // Preprocess points: uniform interpolation for smoothness
  const { points: prePoints, bounds } = preprocessPointsWatercolor({
    points,
    lineWidth,
    flatEdges,
  });
  if (!bounds || prePoints.length === 0) return;

  // Path length and precompute taper/angles
  const totalPathLength = calculatePath(prePoints);
  const orientedPoints = precomputeTaperAndAnglesWatercolor({
    points: prePoints,
    lineWidth,
    taperLength,
    taperWidth,
    totalPathLength,
    flatEdges,
    autoRotation,
    angle,
    edgeStep,
  });

  // Temporary buffer canvas
  const bufferCanvas = document.createElement('canvas');
  bufferCanvas.width = bounds.width;
  bufferCanvas.height = bounds.height;
  const bufferCtx = bufferCanvas.getContext('2d');

  // Bristle/granule parameters
  let granuleCount = Math.round(600 * density);
  let granuleWidth = lineWidth / 25;
  const wobbleAmount = lineWidth * wobble;
  const bloomAmount = lineWidth * bloom * 0.5;
  const granulationFactor = granulation;

  // Generate offsets: elliptic for flat, circular otherwise, with extra for bloom
  const offsets = flatEdges
    ? generateEllipticOffsets(time, granuleCount, lineWidth, 1.5) // Slightly flatter for watercolor flow
    : generateGranuleOffsets(time, lineWidth, granuleCount, granulationFactor);

  bufferCtx.lineCap = 'round';
  bufferCtx.lineJoin = 'round';

  // Group by color and quantized width, with watercolor variations
  const colorWidthGroups = groupOffsetsByColorWatercolor({
    offsets,
    chromaColor,
    opacity,
    time,
    textureStrength,
    dryEdges,
    granulation,
    granuleWidth,
  });

  // Draw groups
  for (const key in colorWidthGroups) {
    const { color: groupColor, lineWidth: groupWidth, items } = colorWidthGroups[key];

    const path = new Path2D();
    bufferCtx.strokeStyle = groupColor;
    bufferCtx.lineWidth = groupWidth;

    const circles = []; // For bloom effect, we can add soft circles

    for (const { offset } of items) {
      const res = appendGranuleToPathWatercolor({
        path,
        points: orientedPoints,
        lineWidth: groupWidth,
        mainLineWidth: lineWidth,
        color: groupColor,
        time,
        offset,
        wobbleAmount,
        dryEdges,
        taperWidth,
        flatEdges,
        bloomAmount,
      });
      if (res?.drewCircles && res.drewCircles.length) circles.push(...res.drewCircles);
    }

    // Fill soft bloom circles first (low opacity)
    if (circles.length && bloom > 0) {
      bufferCtx.fillStyle = chroma(groupColor).alpha(opacity * 0.1).hex(); // Soft bloom
      bufferCtx.beginPath();
      for (const c of circles) {
        const r = Math.max(0.5, c.r || (groupWidth / 1.5));
        bufferCtx.moveTo(c.x + r, c.y);
        bufferCtx.arc(c.x, c.y, r, 0, Math.PI * 2);
      }
      bufferCtx.fill();
    }

    // Then stroke the main path
    bufferCtx.stroke(path);
  }

  // Optional: Apply a light blur for diffusion effect (performance note: small buffer)
  if (bloom > 0.2) {
    bufferCtx.filter = `blur(${Math.min(1.5, lineWidth * 0.02)}px)`;
    bufferCtx.drawImage(bufferCanvas, 0, 0); // Redraw to apply filter
    bufferCtx.filter = 'none';
  }

  // Apply gradient if provided
  if (gradientColor) {
    fillGradient(bufferCtx, orientedPoints, color, gradientColor, lineWidth);
    context.globalAlpha = 1;
  } else {
    context.globalAlpha = originalAlpha;
  }

  // Transfer buffer to main context
  context.drawImage(
    bufferCanvas,
    0, 0, bounds.width, bounds.height,
    bounds.x, bounds.y, bounds.width, bounds.height
  );
  context.globalAlpha = 1;
}

// Preprocess points for watercolor (uniform spacing, handle flat)
function preprocessPointsWatercolor({
  points,
  lineWidth,
  flatEdges,
}) {
  if (points.length === 0) return { points: [], bounds: null };
  const bounds = getStrokeBounds(points, lineWidth * 1.2); // Extra padding for bloom
  if (!bounds) return { points: [], bounds: null };

  const offsetX = -bounds.x;
  const offsetY = -bounds.y;
  let pts = translatePoints(points, offsetX, offsetY);

  let uniformStep = Math.max(2, lineWidth / 6); // Finer spacing for fluid effect
  if (pts[0]?.pressure) { uniformStep = 4; }
  pts = interpolatePointsUniform(pts, uniformStep);

  return { points: pts, bounds };
}

// Precompute taper and angles (similar to wet, but softer taper)
function precomputeTaperAndAnglesWatercolor({
  points,
  lineWidth,
  taperLength,
  taperWidth,
  totalPathLength,
  flatEdges,
  autoRotation,
  angle,
  edgeStep,
}) {
  const pathPoints = calculatePathDistances(points);

  const taperedPoints = pathPoints.map(point => {
    const fromBegin = point.distance;
    const fromEnd = totalPathLength - point.distance;
    const minDistance = Math.min(fromBegin, fromEnd);
    const effectiveDistance = Math.min(totalPathLength / 2, lineWidth * taperLength);
    let basicTaper = 1;
    if (effectiveDistance > 0 && minDistance < effectiveDistance) {
      const t = minDistance / effectiveDistance;
      basicTaper = t * t * t * (t * (t * 6 - 15) + 10); // Smoother ease (Perlin-like)
    }
    const pressureFactor = point.pressure ?? basicTaper;
    const taperStrength = taperWidth;
    const currentTaper = (1 - pressureFactor) * taperStrength;
    const taper = 1 - currentTaper;
    return {
      ...point,
      taper,
      angle: 0,
      pressure: point.pressure ?? 1,
    };
  });

  if (flatEdges) {
    if (autoRotation) {
      return addPathAnglesWithEdgeSkipping(taperedPoints, { edgeStep, totalPathLength });
    } else {
      const fixedAngleRad = angle * (Math.PI / 180);
      return taperedPoints.map(p => ({ ...p, angle: fixedAngleRad }));
    }
  } else {
    return addPathAngles(taperedPoints);
  }
}

// Generate granule offsets with granulation jitter
function generateGranuleOffsets(time, lineWidth, count, granulationFactor) {
  const baseRadius = lineWidth * 0.45;
  let offsets = [];
  for (let i = 0; i < count; i++) {
    const angle = seededRandom(time + i) * Math.PI * 2;
    const distance = Math.sqrt(seededRandom(time + i + 100)) * baseRadius;
    const x = Math.cos(angle) * distance;
    const y = Math.sin(angle) * distance;
    const jitterX = (seededRandom(time + i + 200) - 0.5) * granulationFactor * baseRadius;
    const jitterY = (seededRandom(time + i + 300) - 0.5) * granulationFactor * baseRadius;

    const adjX = x + jitterX;
    const adjY = y + jitterY;
    const adjDist = Math.sqrt(adjX ** 2 + adjY ** 2);

    offsets.push({ x: adjX, y: adjY, distance: adjDist });
  }
  const maxDist = Math.max(...offsets.map(o => o.distance));
  offsets.forEach(o => {
    o.normalizedDistance = maxDist > 0 ? o.distance / maxDist : 0;
    o.edge = 1 - o.normalizedDistance; // Higher edge = closer to boundary
  });
  offsets.sort((a, b) => a.distance - b.distance);
  return offsets;
}

// Group offsets by color/width with watercolor effects (darker edges, granulation)
function groupOffsetsByColorWatercolor({
  offsets, chromaColor, opacity, time, textureStrength, dryEdges, granulation, granuleWidth
}) {
  const groups = {};
  for (let i = 0; i < offsets.length; i++) {
    const offset = offsets[i];
    // Vary brightness more on edges for pigment accumulation
    const edgeBoost = offset.edge * dryEdges * 2;
    const variation = seededRandom(time + offset.x + offset.y) * (textureStrength + edgeBoost) - (textureStrength / 2);
    let currentColor = chroma(chromaColor).darken(variation + edgeBoost * 0.5).alpha(opacity * (0.4 + offset.edge * 0.6 + granulation * 0.2)).hex();

    // Vary width with granulation
    const varyWidth = (w) => w * (0.7 + seededRandom(time + (offset.distance || 0)) * 0.6 + granulation * 0.3);
    const varied = varyWidth(granuleWidth);
    const quantStep = Math.max(0.15, granuleWidth * 0.15);
    const quantizedWidth = Math.max(0.1, Math.round(varied / quantStep) * quantStep);
    const key = `${currentColor}|w:${quantizedWidth.toFixed(2)}`;
    if (!groups[key]) groups[key] = { color: currentColor, lineWidth: quantizedWidth, items: [] };
    groups[key].items.push({ offset, varyWidth });
  }
  return groups;
}

// Append granule path with watercolor specifics (bloom, dry edges)
function appendGranuleToPathWatercolor(stroke) {
  const {
    path,
    points,
    offset = { x: 0, y: 0 },
    wobbleAmount,
    time,
    dryEdges,
    taperWidth,
    flatEdges,
    bloomAmount,
  } = stroke;

  if (points.length === 1) {
    return drawPointWatercolor(stroke);
  }

  let isDrawing = false;
  const offsetSeed = offset.x * offset.y;
  const normDist = offset.normalizedDistance || 0;
  const edge = offset.edge || 0;

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];

    const avgTaper = (prev.taper + curr.taper) / 2;
    const reqEdge = avgTaper * dryEdges;
    const rnd = seededRandom(normDist + i);
    const dev = (rnd - 0.5) * taperWidth;
    const randEdge = edge + dev;

    // For watercolor: prefer edges (skip if not edgy enough)
    const shouldSkip = randEdge < reqEdge;

    let effOffsetX = 0;
    let effOffsetY = 0;

    if (flatEdges) {
      const angle = curr.angle ?? prev.angle ?? 0;
      const nx = Math.cos(angle + Math.PI / 2);
      const ny = Math.sin(angle + Math.PI / 2);
      const tx = Math.cos(angle);
      const ty = Math.sin(angle);
      effOffsetX = (nx * offset.x + tx * (offset.y || 0)) * avgTaper;
      effOffsetY = (ny * offset.x + ty * (offset.y || 0)) * avgTaper;
    } else {
      effOffsetX = offset.x * avgTaper;
      effOffsetY = offset.y * avgTaper;
    }

    if (shouldSkip) {
      isDrawing = false;
      continue;
    }

    const pressure = curr.pressure ?? 1;
    const adjWobble = wobbleAmount * (1 - pressure * 0.4);
    const wobbleX = (seededRandom(time + offsetSeed + i + 200) - 0.5) * adjWobble;
    const wobbleY = (seededRandom(time + offsetSeed + i + 300) - 0.5) * adjWobble;

    const midX = (prev.x + curr.x) / 2 + wobbleX + effOffsetX;
    const midY = (prev.y + curr.y) / 2 + wobbleY + effOffsetY;
    const fromX = prev.x + effOffsetX;
    const fromY = prev.y + effOffsetY;

    if (!isDrawing) {
      path.moveTo(fromX, fromY);
      isDrawing = true;
    }
    path.quadraticCurveTo(fromX, fromY, midX, midY);
  }

  // Return potential bloom circles
  return { drewCircles: [] }; // Can add bloom here if needed
}

// Single point drawing for watercolor
function drawPointWatercolor({
  points, lineWidth, color, offset, dryEdges, flatEdges
}, context) {
  const point = points[0];
  const pressure = point.pressure ?? 1;
  const avgTaper = point.taper ?? 1;

  if ((offset?.edge || 0) < (avgTaper * dryEdges)) {
    return { drewCircles: [] };
  }

  let effOffsetX = 0;
  let effOffsetY = 0;

  if (flatEdges) {
    const angle = point.angle ?? 0;
    const nx = Math.cos(angle + Math.PI / 2);
    const ny = Math.sin(angle + Math.PI / 2);
    const tx = Math.cos(angle);
    const ty = Math.sin(angle);
    effOffsetX = (nx * offset.x + tx * (offset.y || 0)) * avgTaper;
    effOffsetY = (ny * offset.x + ty * (offset.y || 0)) * avgTaper;
  } else {
    effOffsetX = offset.x * avgTaper;
    effOffsetY = offset.y * avgTaper;
  }

  const adjRadius = (lineWidth / 2) * pressure * avgTaper;

  return {
    drewCircles: [{
      x: point.x + effOffsetX,
      y: point.y + effOffsetY,
      r: adjRadius
    }]
  };
}

// Reused functions
function seededRandom(seed) {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

function addPathAngles(points) {
  if (!points || points.length === 0) return points;
  const out = new Array(points.length);
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    let dx = 0, dy = 0;
    if (i > 0) {
      dx += p.x - points[i - 1].x;
      dy += p.y - points[i - 1].y;
    }
    if (i < points.length - 1) {
      dx += points[i + 1].x - p.x;
      dy += points[i + 1].y - p.y;
    }
    const angle = Math.atan2(dy, dx);
    out[i] = { ...p, angle };
  }
  return out;
}

function addPathAnglesWithEdgeSkipping(points, { edgeStep = 4, totalPathLength }) {
  if (!points || points.length === 0) return points;
  const n = points.length;
  const out = new Array(n);
  const clampIndex = (idx) => Math.max(0, Math.min(n - 1, idx));

  for (let i = 0; i < n; i++) {
    const p = points[i];
    const distanceFromEnd = totalPathLength - p.distance;

    const isInStartWindow = p.distance < distanceFromEnd && p.distance < 20;
    const isInEndWindow = p.distance > distanceFromEnd && distanceFromEnd < 20;

    let dx = 0, dy = 0;

    if (isInStartWindow) {
      const j = clampIndex(i + edgeStep);
      dx = points[j].x - p.x;
      dy = points[j].y - p.y;
    } else if (isInEndWindow) {
      const j = clampIndex(i - edgeStep);
      dx = p.x - points[j].x;
      dy = p.y - points[j].y;
    } else {
      const j = clampIndex(i + 1);
      dx = points[j].x - p.x;
      dy = points[j].y - p.y;
    }
    const angle = Math.atan2(dy, dx);
    out[i] = { ...p, angle };
  }
  return out;
}

function generateEllipticOffsets(time, count, radius, flatness) {
  const a = radius / 2;
  const b = radius / (2 + flatness * flatness);
  const arr = [];
  for (let i = 0; i < count; i++) {
      const r = Math.sqrt(seededRandom(time + i * 17.11));
      const theta = seededRandom(time + i * 9.73) * Math.PI * 2;
      const x = r * Math.cos(theta) * a;
      const y = r * Math.sin(theta) * b;
      const distance = Math.sqrt(x * x + y * y);
      const edge = Math.min(1, Math.sqrt((x * x) / (a * a) + (y * y) / (b * b)));
      arr.push({ x, y, distance, normalizedDistance: edge });
  }
  arr.sort((a, b) => a.distance - b.distance);
  return arr;
}